import type {
  MemoryFeastBowl,
  MemoryFeastPhase,
  MemoryFeastPlayerId,
  MemoryFeastState,
  MemoryFeastViewerState,
} from "../types";
import {
  MEMORY_FEAST_BONUS_TOKENS,
  MEMORY_FEAST_BOWL_COUNT,
  MEMORY_FEAST_PLAYERS,
} from "../types";

export function isPlayerId(value: unknown): value is MemoryFeastPlayerId {
  return (
    typeof value === "string" &&
    MEMORY_FEAST_PLAYERS.includes(value as MemoryFeastPlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: MemoryFeastPlayerId,
): MemoryFeastPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isBowlId(value: number): boolean {
  return (
    Number.isInteger(value) && value >= 0 && value < MEMORY_FEAST_BOWL_COUNT
  );
}

export function isPlacementPhase(phase: MemoryFeastPhase): boolean {
  return phase === "place" || phase === "place-bonus";
}

/** How many tokens the active player places this turn. */
export function roundTokens(state: MemoryFeastState): number {
  return state.phase === "place-bonus"
    ? MEMORY_FEAST_BONUS_TOKENS
    : state.round;
}

/** Add +1 to each listed bowl, returning a new bowls array. */
export function addTokens(
  bowls: readonly MemoryFeastBowl[],
  bowlIds: readonly number[],
): readonly MemoryFeastBowl[] {
  const bump = new Set(bowlIds);
  return bowls.map((bowl) =>
    bump.has(bowl.id) ? { ...bowl, count: bowl.count + 1 } : bowl,
  );
}

/** Set exactly the listed bowls open, all others covered. */
export function setBowlsOpen(
  bowls: readonly MemoryFeastBowl[],
  openIds: readonly number[],
): readonly MemoryFeastBowl[] {
  const open = new Set(openIds);
  return bowls.map((bowl) => ({ ...bowl, open: open.has(bowl.id) }));
}

export function getBowl(
  bowls: readonly MemoryFeastBowl[],
  id: number,
): MemoryFeastBowl | undefined {
  return bowls[id];
}

/**
 * Observer/viewer projection: covered bowls have their count masked (null);
 * only currently-open bowls reveal their count. Hidden information stays
 * server-authoritative — never project a covered bowl's true count.
 */
export function filterStateForViewer(
  state: MemoryFeastState,
  _viewerPlayerId: string | null,
): MemoryFeastViewerState {
  return {
    ...state,
    bowls: state.bowls.map((bowl) => ({
      id: bowl.id,
      open: bowl.open,
      count: bowl.open ? bowl.count : null,
    })),
  };
}

export function buildViewerStates(
  state: MemoryFeastState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, MemoryFeastViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
